/* v7
 * AdminPage.tsx — Route: /admin
 *
 * Changes from v6:
 * - Added device-based analytics (DAU/MAU, new vs returning)
 *   using get_daily_active_devices, get_new_vs_returning, get_monthly_active_devices RPCs
 * - Traffic chart now shows unique devices per day (not just sessions)
 * - New "Audience" section at top of analytics tab
 */

import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { supabase } from '../lib/supabase'
import type { Venue } from '../types'
import { getVenues } from '../services/venueService'
import { getAllBrandAds, saveBrandAd, deleteBrandAd, toggleBrandAd } from '../services/brandAdService'
import type { BrandAd } from '../components/SponsoredBanner'
import { EditVenueForm } from '../components/EditVenueForm'
import { WeeklyFeatureManager } from '../components/WeeklyFeatureManager'

interface VenueStats {
  venue_id: string; card_views: number; detail_views: number
  directions_clicks: number; website_clicks: number
  favorites: number; confirmations: number; week_start: string
}

interface Contribution {
  id: string; flow: 'new_venue' | 'suggest_edit' | 'claim_venue'
  data: any; status: 'pending' | 'approved' | 'rejected'; created_at: string
}

// A submission counts as a claim if it's explicitly tagged that way, OR
// if it carries owner contact fields under the older overloaded
// 'suggest_edit' flow (how claims were submitted before this fix existed —
// this heuristic reclassifies those historical rows without a data
// migration; safe because SuggestEditForm never sends owner_* fields).
function isClaimContribution(c: Contribution): boolean {
  return c.flow === 'claim_venue' || !!(c.data?.owner_email || c.data?.owner_name)
}

// ── Device analytics state shape ─────────────────────────────────────────────

interface DeviceStats {
  mau: number
  todayDevices: number
  newDevices30d: number
  returningDevices30d: number
  dailyDevices: { date: string; devices: number }[]
}

// ── Simple inline toast ──────────────────────────────────────────────────────

interface Toast { id: number; message: string; type: 'success' | 'error' | 'info' }

function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([])
  let counter = 0
  function show(message: string, type: Toast['type'] = 'info') {
    const id = Date.now() + counter++
    setToasts(prev => [...prev, { id, message, type }])
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500)
  }
  return { toasts, show }
}

function ToastContainer({ toasts }: { toasts: Toast[] }) {
  if (!toasts.length) return null
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding: '12px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
          background: t.type === 'success' ? '#DCFCE7' : t.type === 'error' ? '#fee2e2' : '#EFF6FF',
          color: t.type === 'success' ? '#15803D' : t.type === 'error' ? '#c0392b' : '#1E40AF',
          border: `1px solid ${t.type === 'success' ? '#bbf7d0' : t.type === 'error' ? '#fecaca' : '#bfdbfe'}`,
          boxShadow: '0 4px 12px rgba(0,0,0,.1)', maxWidth: 320,
        }}>
          {t.type === 'success' ? '✓ ' : t.type === 'error' ? '✕ ' : 'ℹ '}{t.message}
        </div>
      ))}
    </div>
  )
}

// ── Style helpers ────────────────────────────────────────────────────────────

const btn = (bg: string, color: string, extra?: React.CSSProperties): React.CSSProperties => ({
  border: 'none', borderRadius: 8, padding: '7px 13px', fontSize: 12,
  fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', background: bg,
  color, flexShrink: 0, ...extra,
})

const card: React.CSSProperties = {
  background: '#fff', border: '1px solid #EAE6DF', borderRadius: 14,
  padding: '16px 18px', marginBottom: 10,
}

function Toggle({ on, onChange, label, color }: {
  on: boolean; onChange: (v: boolean) => void; label: string; color: string
}) {
  return (
    <div onClick={() => onChange(!on)} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}>
      <div style={{ width: 36, height: 20, borderRadius: 20, background: on ? color : '#E0DDD8', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: 2, left: on ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color: on ? color : '#aaa' }}>{label}</span>
    </div>
  )
}

function StatCard({ label, value, color, sub }: { label: string; value: number | string; color: string; sub?: string }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', border: '1px solid #EAE6DF', textAlign: 'center' }}>
      <div style={{ fontSize: 24, fontWeight: 800, color, marginBottom: 2 }}>{value}</div>
      <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
      {sub && <div style={{ fontSize: 9, color: '#ccc', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      padding: '9px 20px', borderRadius: '10px 10px 0 0', border: 'none',
      borderBottom: active ? '3px solid #E85D1A' : '3px solid transparent',
      cursor: 'pointer', fontSize: 13, fontWeight: 700,
      background: active ? '#fff' : 'transparent',
      color: active ? '#1A1612' : '#888',
    }}>{children}</button>
  )
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(false)
  const [authLoading, setAuthLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [loggingIn, setLoggingIn] = useState(false)

  const [tab, setTab] = useState<'pending' | 'analytics' | 'ads' | 'weekly'>('pending')

  const [contributions, setContributions] = useState<Contribution[]>([])
  const [contribLoading, setContribLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending')

  const [brandAds, setBrandAds] = useState<BrandAd[]>([])
  const [editingAd, setEditingAd] = useState<Partial<BrandAd> | null>(null)
  const [adSaving, setAdSaving] = useState(false)
  const [adStats, setAdStats] = useState<Record<string, { impressions: number; clicks: number; days: number; ctr: number }>>({})

  const [venues, setVenues] = useState<Venue[]>([])
  const [stats, setStats] = useState<Record<string, VenueStats>>({})
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [trafficData, setTrafficData] = useState<{ date: string; visitors: number; sessions: number }[]>([])
  const [sending, setSending] = useState<string | null>(null)
  // ONE range drives everything on this tab — top stat cards, the venue
  // list's clicks/favorites, and impressions/venue-views. Previously two
  // independent systems (a week/month toggle here, a separate
  // today/week/month/alltime toggle further down) could be set to
  // different ranges at the same time, silently showing numbers from
  // two different windows on the same screen.
  const [statsRange, setStatsRange] = useState<'week' | 'month' | 'year'>('week')
  const [rangeOffset, setRangeOffset] = useState(0)
  const [impressionData,   setImpressionData]   = useState<Record<string, number>>({})
  // Top Venues panel gets its own range, independent of the general
  // Venue Appearances toggle further down — that one also drives the
  // full scrollable venue list, and someone may want a quick weekly
  // top-10 snapshot while looking at the full month elsewhere on the
  // same page.
  const [topVenuesRange, setTopVenuesRange] = useState<'week' | 'month'>('week')
  const [topVenuesData,  setTopVenuesData]  = useState<Record<string, number>>({})
  const [detailViewData,   setDetailViewData]   = useState<Record<string, number>>({})
  const [last7Visits,      setLast7Visits]      = useState<number>(0)
  const [todayDeviceCount, setTodayDeviceCount] = useState<number>(0)
  const [monthlyGrowth, setMonthlyGrowth] = useState<{ rate: number | null; thisMonth: number; lastMonth: number; thisMonthLabel: string; lastMonthLabel: string }>({ rate: null, thisMonth: 0, lastMonth: 0, thisMonthLabel: '', lastMonthLabel: '' })
  const [dealsViews, setDealsViews] = useState<number>(0)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'views' | 'clicks'>('name')
  const [toggling, setToggling] = useState<string | null>(null)
  const [editingVenueId, setEditingVenueId] = useState<string | null>(null)


  // ── NEW: device-based analytics ──────────────────────────────────────────
  const [deviceStats, setDeviceStats] = useState<DeviceStats | null>(null)
  const [deviceStatsLoading, setDeviceStatsLoading] = useState(false)

  const { toasts, show: showToast } = useToast()

  // ── Auth ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthed(!!session)
      setAuthLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthed(!!session)
    })
    return () => subscription.unsubscribe()
  }, [])

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setLoginError('')
    setLoggingIn(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setLoginError('Incorrect email or password')
    setLoggingIn(false)
  }

  async function handleLogout() { await supabase.auth.signOut() }

  // ── Data loading ─────────────────────────────────────────────────────────

  async function loadContributions() {
    setContribLoading(true)
    const { data } = await supabase.from('contributions').select('*').order('created_at', { ascending: false }).limit(5000)
    if (data) setContributions(data)
    setContribLoading(false)
  }

  // ── NEW: load device analytics from the RPCs we created ─────────────────
  async function loadDeviceStats() {
    setDeviceStatsLoading(true)
    try {
      // Single RPC — one call, one timezone, one date format, no mismatches
      const { data, error } = await supabase.rpc('get_analytics_summary')
      if (error) throw error
      if (!data) throw new Error('No data returned')

      // Build daily chart array from JSON payload
      const dailySessions: Array<{ date: string; sessions: number }> =
        Array.isArray(data.daily_sessions) ? data.daily_sessions : []

      // Map to the format the chart expects: { date: 'Jul 1', visitors, sessions }
      const trafficDays = dailySessions.map((d: any) => {
        const dt    = new Date(d.date + 'T12:00:00')  // noon local avoids UTC edge case
        const label = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        return { date: label, visitors: Number(d.sessions) || 0, sessions: Number(d.sessions) || 0 }
      })

      setTrafficData(trafficDays)
      setLast7Visits(Number(data.total_visits_7days) || 0)
      setTodayDeviceCount(Number(data.today_devices) || 0)
      setDeviceStats({
        mau:                Number(data.mau)               || 0,
        todayDevices:       Number(data.today_devices)     || 0,
        newDevices30d:      Number(data.new_devices)       || 0,
        returningDevices30d:Number(data.returning_devices) || 0,
        dailyDevices:       [],   // device chart removed — using session chart only
      })

    } catch (e) {
      console.error('loadDeviceStats error:', e)
    }
    setDeviceStatsLoading(false)
  }
  // Single source of truth for "what date window is currently selected,"
  // reused by both the venue_stats fetch and the impressions RPC below —
  // previously these could each be computed from a DIFFERENT independent
  // toggle, silently showing two different windows on one screen.
  function computeStatsRange(mode: 'week' | 'month' | 'year', offset: number): { start: Date; end: Date } {
    if (mode === 'week') {
      const start = new Date()
      start.setDate(start.getDate() - 7 + (offset * 7))
      start.setHours(0, 0, 0, 0)
      const end = new Date(start)
      end.setDate(end.getDate() + 7)
      return { start, end }
    }
    if (mode === 'month') {
      const base = new Date()
      base.setMonth(base.getMonth() + offset, 1)
      return {
        start: new Date(base.getFullYear(), base.getMonth(), 1),
        end: new Date(base.getFullYear(), base.getMonth() + 1, 1),
      }
    }
    // year
    const base = new Date()
    base.setFullYear(base.getFullYear() + offset, 0, 1)
    return {
      start: new Date(base.getFullYear(), 0, 1),
      end: new Date(base.getFullYear() + 1, 0, 1),
    }
  }

  async function loadAnalytics() {
    setAnalyticsLoading(true)
    // Traffic chart handled by loadDeviceStats via get_analytics_summary
    try {
      const venueList = await getVenues('Cincinnati')
      setVenues(venueList)

      const { start: rangeStart, end: rangeEnd } = computeStatsRange(statsRange, rangeOffset)

      const { data: appearances, error: appearancesErr } = await supabase.rpc(
        'get_venue_impression_counts',
        { since_ts: rangeStart.toISOString(), until_ts: rangeEnd.toISOString() }
      )
      if (appearancesErr) console.error('appearances RPC error:', appearancesErr)

      const statsMap: Record<string, VenueStats> = {}

      if (statsRange === 'week') {
        const { data: statsData } = await supabase
          .from('venue_stats').select('*').eq('week_start', getWeekStart(rangeOffset))

        if (statsData) {
          statsData.forEach((s: VenueStats) => {
            statsMap[s.venue_id] = {
              ...s,
              // Always recount views from venue_appearances — venue_stats can drift
              card_views: 0,
              detail_views: 0,
            }
          })
        }
      } else {
        // Month/year mode: pull every weekly row whose week_start falls
        // inside the target range and SUM them per venue — a venue can
        // have up to 5 (month) or ~52 (year) week_start rows in range,
        // and this table has no coarser grain of its own.
        // Explicit high limit — without one, Supabase's default 1000-row
        // cap silently truncates this the same way it truncated the
        // impressions query earlier tonight. 75 venues × 52 weeks alone
        // is already ~3,900 rows for a full year — comfortably past the
        // default cap today, not just "eventually." A server-side SUM
        // (same pattern as get_venue_impression_counts) would be the
        // more scalable long-term fix if venue count grows much further.
        const { data: statsData } = await supabase
          .from('venue_stats').select('*')
          .gte('week_start', rangeStart.toISOString().split('T')[0])
          .lt('week_start', rangeEnd.toISOString().split('T')[0])
          .limit(10000)

        if (statsData) {
          statsData.forEach((s: VenueStats) => {
            if (!statsMap[s.venue_id]) {
              statsMap[s.venue_id] = { ...s, card_views: 0, detail_views: 0 }
            } else {
              statsMap[s.venue_id].directions_clicks += s.directions_clicks || 0
              statsMap[s.venue_id].website_clicks    += s.website_clicks || 0
              statsMap[s.venue_id].favorites         += s.favorites || 0
              statsMap[s.venue_id].confirmations     += s.confirmations || 0
            }
          })
        }
      }

      // Same RPC result feeds BOTH the venue-list's card_views/detail_views
      // (which "Deals Viewed" reads from) AND the per-card
      // Impressions/Venue Views numbers below — one fetch, one range,
      // instead of the two separately-fetched systems this replaced.
      const cardCounts:   Record<string, number> = {}
      const detailCounts: Record<string, number> = {}
      if (appearances) {
        appearances.forEach((r: any) => {
          if (!statsMap[r.venue_id]) {
            statsMap[r.venue_id] = {
              venue_id: r.venue_id, card_views: 0, detail_views: 0,
              directions_clicks: 0, website_clicks: 0, favorites: 0,
              confirmations: 0, week_start: getWeekStart(rangeOffset),
            }
          }
          if (r.event_type === 'card_view') {
            statsMap[r.venue_id].card_views = (statsMap[r.venue_id].card_views || 0) + Number(r.cnt)
            cardCounts[r.venue_id] = (cardCounts[r.venue_id] || 0) + Number(r.cnt)
          } else if (r.event_type === 'detail_view') {
            statsMap[r.venue_id].detail_views = (statsMap[r.venue_id].detail_views || 0) + Number(r.cnt)
            detailCounts[r.venue_id] = (detailCounts[r.venue_id] || 0) + Number(r.cnt)
          }
        })
      }
      setImpressionData(cardCounts)
      setDetailViewData(detailCounts)
      setStats(statsMap)
    } catch (e) { console.error(e) }
    setAnalyticsLoading(false)
  }

  async function loadGrowthAndDeals() {
    const now = new Date()

    // Calendar month boundaries
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1)
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const lastMonthEnd   = new Date(now.getFullYear(), now.getMonth(), 1) // exclusive

    // Deals viewed this calendar month
    const d30 = new Date(now.getFullYear(), now.getMonth(), 1)

    const [
      { count: thisMonth },
      { count: lastMonth },
      { count: dealsCount },
    ] = await Promise.all([
      // This calendar month appearances — server-side count avoids 1000 row cap
      supabase.from('venue_impressions').select('*', { count: 'exact', head: true })
        .gte('created_at', thisMonthStart.toISOString())
        .eq('event_type', 'card_view'),
      // Last calendar month appearances
      supabase.from('venue_impressions').select('*', { count: 'exact', head: true })
        .gte('created_at', lastMonthStart.toISOString())
        .lt('created_at', lastMonthEnd.toISOString())
        .eq('event_type', 'card_view'),
      // Deals viewed — last 30 days
      supabase.from('venue_impressions').select('*', { count: 'exact', head: true })
        .gte('created_at', d30.toISOString())
        .eq('event_type', 'detail_view'),
    ])

    const thisMonthCount = thisMonth ?? 0
    const lastMonthCount = lastMonth ?? 0
    const rate = lastMonthCount > 0 ? Math.round(((thisMonthCount - lastMonthCount) / lastMonthCount) * 100) : null

    // Label the months for clarity
    const thisMonthLabel = now.toLocaleString('default', { month: 'short' })
    const lastMonthLabel = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      .toLocaleString('default', { month: 'short' })

    setMonthlyGrowth({ rate, thisMonth: thisMonthCount, lastMonth: lastMonthCount, thisMonthLabel, lastMonthLabel })
    setDealsViews(dealsCount ?? 0)
  }

  async function loadTopVenues(range: 'week' | 'month') {
    const since = new Date()
    if (range === 'week') since.setDate(since.getDate() - 7)
    else { since.setDate(1); since.setHours(0, 0, 0, 0) }  // calendar month start

    const { data, error } = await supabase.rpc('get_venue_impression_counts', { since_ts: since.toISOString() })
    if (error) { console.error('loadTopVenues error:', error); return }

    const counts: Record<string, number> = {}
    ;(data ?? []).forEach((r: any) => {
      if (r.event_type === 'card_view' || !r.event_type) {
        counts[r.venue_id] = (counts[r.venue_id] || 0) + Number(r.cnt)
      }
    })
    setTopVenuesData(counts)
  }


  async function loadBrandAds() {
    const ads = await getAllBrandAds()
    setBrandAds(ads)
    const since = new Date(); since.setDate(since.getDate() - 30)
    const { data } = await supabase.from('ad_events')
      .select('ad_id, brand_name, event_type, created_at').gte('created_at', since.toISOString()).limit(20000)
    if (data) {
      const stats: Record<string, { impressions: number; clicks: number; days: Set<string> }> = {}
      data.forEach((e: any) => {
        if (!stats[e.ad_id]) stats[e.ad_id] = { impressions: 0, clicks: 0, days: new Set() }
        if (e.event_type === 'impression') {
          stats[e.ad_id].impressions++
          stats[e.ad_id].days.add(e.created_at.split('T')[0])
        } else if (e.event_type === 'click') {
          stats[e.ad_id].clicks++
        }
      })
      const simplified: Record<string, { impressions: number; clicks: number; days: number; ctr: number }> = {}
      Object.entries(stats).forEach(([id, s]) => {
        simplified[id] = {
          impressions: s.impressions,
          clicks:      s.clicks,
          days:        s.days.size,
          ctr:         s.impressions > 0 ? Math.round((s.clicks / s.impressions) * 1000) / 10 : 0,
        }
      })
      setAdStats(simplified)
    }
  }

  // ── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (authed) {
      loadContributions()
      loadAnalytics()
      loadDeviceStats()   // ← NEW
      loadBrandAds()
      loadGrowthAndDeals()
    }
  }, [authed]) // eslint-disable-line

  useEffect(() => { if (authed) loadTopVenues(topVenuesRange) }, [topVenuesRange, authed]) // eslint-disable-line
  useEffect(() => { if (authed) loadAnalytics() }, [statsRange, rangeOffset, authed]) // eslint-disable-line

  // ── Helpers ──────────────────────────────────────────────────────────────

  function getWeekStart(offset = 0): string {
    const d = new Date(); const day = d.getDay()
    d.setDate(d.getDate() - day + (day === 0 ? -6 : 1) + offset * 7)
    d.setHours(0,0,0,0)
    return d.toISOString().split('T')[0]
  }

  function getWeekEnd(offset = 0): string {
    const d = new Date(getWeekStart(offset)); d.setDate(d.getDate() + 6)
    return d.toISOString().split('T')[0]
  }

  function getWeekLabel() {
    if (statsRange === 'year') {
      const base = new Date()
      base.setFullYear(base.getFullYear() + rangeOffset, 0, 1)
      return rangeOffset === 0 ? `This year (${base.getFullYear()})` : `${base.getFullYear()}`
    }
    if (statsRange === 'month') {
      const base = new Date()
      base.setMonth(base.getMonth() + rangeOffset, 1)
      const label = base.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      return rangeOffset === 0 ? `This month (${label})` : label
    }
    if (rangeOffset === 0) return 'Last 7 days'
    if (rangeOffset === -1) return 'Prior 7 days'
    return `Week of ${getWeekStart(rangeOffset)}`
  }

  function getVenueStat(venueId: string): VenueStats {
    return stats[venueId] ?? {
      venue_id: venueId, card_views: 0, detail_views: 0,
      directions_clicks: 0, website_clicks: 0, favorites: 0,
      confirmations: 0, week_start: getWeekStart(rangeOffset),
    }
  }

  const filteredContribs = contributions.filter(c => filter === 'all' ? true : c.status === filter)
  const pendingCount = contributions.filter(c => c.status === 'pending').length

  const filteredVenues = venues
    .filter(v =>
      v.name.toLowerCase().includes(search.toLowerCase()) ||
      v.neighborhood.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name)
      if (sortBy === 'views') return getVenueStat(b.id).detail_views - getVenueStat(a.id).detail_views
      return getVenueStat(b.id).directions_clicks - getVenueStat(a.id).directions_clicks
    })


  const totalDirections = venues.reduce((acc, v) => acc + getVenueStat(v.id).directions_clicks, 0)
  const totalFavorites = venues.reduce((acc, v) => acc + getVenueStat(v.id).favorites, 0)
  const totalDealsViewed = venues.reduce((acc, v) => acc + (getVenueStat(v.id).detail_views || 0), 0)

  // ── Actions ──────────────────────────────────────────────────────────────

  async function approveVenue(contrib: Contribution) {
    setActionLoading(contrib.id)
    try {
      const d = contrib.data
      function sanitizeDeal(deal: any) {
        const VALID_TYPES = ['beer', 'cocktail', 'liquor', 'wine', 'food', 'general']
        const VALID_VALUE_TYPES = ['price', 'amount_off', 'percent_off']
        // NewVenueForm now submits value/value_type for $-off and %-off
        // deals, not just a flat price. Without reading them here, every
        // "$2 off drafts" submission got approved into the live venues
        // table with price forced to null and the discount silently
        // dropped — reverting to the exact ambiguity this feature fixes,
        // right at the one step (publish) an admin can't see happen.
        const valueType = VALID_VALUE_TYPES.includes(deal.value_type) ? deal.value_type : 'price'
        const rawValue = typeof deal.value === 'number' && deal.value >= 0 && deal.value <= 999 ? deal.value : null
        return {
          type: VALID_TYPES.includes(deal.type) ? deal.type : 'general',
          description: String(deal.description ?? '').slice(0, 200),
          price: valueType === 'price'
            ? (typeof deal.price === 'number' && deal.price >= 0 && deal.price <= 999 ? deal.price : rawValue)
            : null,
          value: rawValue,
          value_type: valueType,
        }
      }
      const { data: venue, error: vErr } = await supabase.from('venues').insert([{
        name: d.name?.trim(), neighborhood: d.neighborhood?.trim() || 'Cincinnati',
        city: d.city || 'Cincinnati', state: 'OH',
        address: d.address?.trim() || null, website: d.website?.trim() || null,
        phone: d.phone?.trim() || null, latitude: d.latitude || null, longitude: d.longitude || null,
        dog_friendly: d.dog_friendly || false, business_hours: d.business_hours || null, categories: Array.isArray(d.categories) ? d.categories : [], price_tier: null, image_url: null,
        verification_status: 'community', data_source: 'user_submitted',
        is_featured: false, is_sponsored: false, upvote_count: 0,
        last_verified_at: new Date().toISOString(),
        created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
      }]).select().single()
      if (vErr) throw new Error(vErr.message)
      const submittedSchedules = d.schedules || []
      if (submittedSchedules.length > 0) {
        await supabase.from('happy_hour_schedules').insert(
          submittedSchedules.map((s: any) => ({
            venue_id: venue.id, days: s.days,
            start_time: s.start_time || '16:00', end_time: s.end_time || '19:00',
            is_all_day: s.is_all_day || false,
            deal_text: String(s.deal_text || '').slice(0, 500),
            deals: Array.isArray(s.deals) ? s.deals.slice(0, 20).map(sanitizeDeal) : [],
            created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
          }))
        )
      } else if (d.deal_details || d.schedule_description) {
        await supabase.from('happy_hour_schedules').insert([{
          venue_id: venue.id, days: ['Mon','Tue','Wed','Thu','Fri'],
          start_time: '16:00', end_time: '19:00', is_all_day: false,
          deal_text: String(d.deal_details || d.schedule_description || '').slice(0, 500),
          deals: [], created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
        }])
      }
      await supabase.from('contributions').update({ status: 'approved' }).eq('id', contrib.id)
      setContributions(prev => prev.map(c => c.id === contrib.id ? { ...c, status: 'approved' } : c))
      showToast(`${d.name || 'Venue'} is now live!`, 'success')
    } catch (e: any) { showToast('Error: ' + e.message, 'error') }
    setActionLoading(null)
  }

  async function approveClaim(contrib: Contribution) {
    setActionLoading(contrib.id)
    try {
      const d = contrib.data
      if (!d.venue_id) throw new Error('Missing venue_id on this claim — cannot link it to a venue')

      // The durable write: this is what "so we have future info and
      // contact" actually means — a row that outlives the contributions
      // queue and stays queryable per-venue going forward.
      const { error: ownerErr } = await supabase.from('venue_owners').insert([{
        venue_id: d.venue_id,
        owner_name: d.owner_name || 'Unknown',
        owner_role: d.owner_role || null,
        owner_email: d.owner_email || null,
        owner_phone: d.owner_phone || null,
        notes: d.notes || null,
        source_contribution_id: contrib.id,
      }])
      if (ownerErr) throw new Error(ownerErr.message)

      const { error: statusErr } = await supabase.from('contributions').update({ status: 'approved' }).eq('id', contrib.id)
      if (statusErr) throw new Error(statusErr.message)

      setContributions(prev => prev.map(c => c.id === contrib.id ? { ...c, status: 'approved' } : c))
      showToast(`${d.owner_name || 'Owner'} linked to ${d.venue_name || 'venue'}`, 'success')
    } catch (e: any) {
      showToast('Error: ' + e.message, 'error')
    }
    setActionLoading(null)
  }

  async function markEditHandled(contrib: Contribution) {
    // Distinct from reject: this means "I read it and made the change
    // myself in Edit Venue" — not "this suggestion was wrong." Using
    // rejectContribution here would mislabel a handled suggestion as
    // rejected in the status badge and filters.
    setActionLoading(contrib.id)
    const { error } = await supabase.from('contributions').update({ status: 'approved' }).eq('id', contrib.id)
    if (!error) {
      setContributions(prev => prev.map(c => c.id === contrib.id ? { ...c, status: 'approved' } : c))
      showToast('Marked as reviewed', 'success')
    } else showToast('Failed: ' + error.message, 'error')
    setActionLoading(null)
  }

  async function rejectContribution(contrib: Contribution) {
    if (!window.confirm('Reject this submission?')) return
    setActionLoading(contrib.id)
    const { error } = await supabase.from('contributions').update({ status: 'rejected' }).eq('id', contrib.id)
    if (!error) setContributions(prev => prev.map(c => c.id === contrib.id ? { ...c, status: 'rejected' } : c))
    else showToast('Failed to reject: ' + error.message, 'error')
    setActionLoading(null)
  }

  async function deleteContribution(id: string) {
    if (!window.confirm('Delete this submission permanently?')) return
    await supabase.from('contributions').delete().eq('id', id)
    setContributions(prev => prev.filter(c => c.id !== id))
  }

  async function toggleVenueFlag(venue: Venue, field: 'is_featured' | 'is_sponsored') {
    const newVal = !(venue as any)[field]
    setToggling(venue.id + field)
    const { error } = await supabase.from('venues').update({ [field]: newVal }).eq('id', venue.id)
    if (!error) setVenues(prev => prev.map(v => v.id === venue.id ? { ...v, [field]: newVal } : v))
    setToggling(null)
  }

  async function deleteVenue(venue: Venue) {
    if (!window.confirm(`Delete "${venue.name}"? This cannot be undone.`)) return
    const { error } = await supabase.from('venues').delete().eq('id', venue.id)
    if (error) { showToast('Delete failed: ' + error.message, 'error'); return }
    setVenues(prev => prev.filter(v => v.id !== venue.id))
    showToast(`${venue.name} deleted`, 'info')
  }

  async function sendStatsEmail(venue: Venue) {
    const s = getVenueStat(venue.id)
    setSending(venue.id)
    const subject = `Your Happy Hour Unlocked listing performance — ${getWeekLabel()}`
    const body = `Hi ${venue.name},\n\nHere's how your listing performed on Happy Hour Unlocked ${getWeekLabel().toLowerCase()}:\n\n📱 Venue appearances: ${s.card_views}\n👀 Detail views: ${s.detail_views}\n🗺️ Directions: ${s.directions_clicks}\n🌐 Website: ${s.website_clicks}\n♥ Saves: ${s.favorites}\n✓ Confirmations: ${s.confirmations}\n\nView your listing: ${window.location.origin}/venue/${venue.id}\n\n— The Happy Hour Unlocked Team`
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank')
    setSending(null)
  }

  async function handleToggleAd(ad: BrandAd) {
    await toggleBrandAd(ad.id, !ad.is_active)
    setBrandAds(prev => prev.map(a => a.id === ad.id ? { ...a, is_active: !a.is_active } : a))
  }

  async function handleDeleteAd(id: string) {
    if (!window.confirm('Delete this ad?')) return
    await deleteBrandAd(id)
    setBrandAds(prev => prev.filter(a => a.id !== id))
  }

  async function handleSaveAd() {
    if (!editingAd?.brand_name || !editingAd?.headline) { showToast('Brand name and headline are required', 'error'); return }
    setAdSaving(true)
    const saved = await saveBrandAd({ ...editingAd, is_active: editingAd.is_active ?? false, position: editingAd.position ?? brandAds.length })
    if (saved) {
      if (editingAd.id) setBrandAds(prev => prev.map(a => a.id === saved.id ? saved : a))
      else setBrandAds(prev => [...prev, saved])
      setEditingAd(null)
      showToast('Ad saved', 'success')
    } else showToast('Failed to save ad', 'error')
    setAdSaving(false)
  }

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; if (!file) return
    const fileExt = file.name.split('.').pop()
    const fileName = `brand-logos/${Date.now()}.${fileExt}`
    const { error } = await supabase.storage.from('public').upload(fileName, file, { upsert: true })
    if (error) { showToast(`Logo upload failed: ${error.message}`, 'error'); return }
    const { data: urlData } = supabase.storage.from('public').getPublicUrl(fileName)
    setEditingAd(prev => ({ ...prev, logo_url: urlData.publicUrl }))
    showToast('Logo uploaded', 'success')
  }

  const EMPTY_AD: Partial<BrandAd> = { brand_name: '', headline: '', subtext: '', logo_url: '', logo_bg_color: '#E85D1A', is_active: false, position: 0 }

  // ── Loading ───────────────────────────────────────────────────────────────

  if (authLoading) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8F6F1' }}><div style={{ fontSize: 13, color: '#888' }}>Loading...</div></div>
  }

  if (!authed) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8F6F1', padding: 24 }}>
        <div style={{ background: '#fff', borderRadius: 20, padding: '32px 28px', width: '100%', maxWidth: 360, border: '1px solid #EAE6DF' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🍺</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#1A1612', letterSpacing: -.5, fontStyle: 'italic' }}>
              <span>Happy Hour</span><span style={{ color: '#E85D1A' }}> Unlocked</span>
            </div>
            <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>Admin Dashboard</div>
          </div>
          <form onSubmit={handleLogin}>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Admin email" required autoFocus
              style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #E0DDD8', fontSize: 15, marginBottom: 10, fontFamily: 'inherit', boxSizing: 'border-box' as const }} />
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" required
              style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #E0DDD8', fontSize: 15, marginBottom: 12, fontFamily: 'inherit', boxSizing: 'border-box' as const }} />
            {loginError && <div style={{ color: '#c0392b', fontSize: 13, marginBottom: 10 }}>{loginError}</div>}
            <button type="submit" disabled={loggingIn} style={{ width: '100%', padding: 13, background: '#E85D1A', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', opacity: loggingIn ? .7 : 1 }}>
              {loggingIn ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
          <p style={{ fontSize: 11, color: '#bbb', textAlign: 'center', marginTop: 16 }}>Use your Supabase admin account credentials</p>
        </div>
      </div>
    )
  }

  // ── DASHBOARD ────────────────────────────────────────────────────────────

  return (
    <>
      <Helmet><title>Admin — Happy Hour Unlocked</title></Helmet>
      <ToastContainer toasts={toasts} />

      <div style={{ maxWidth: 920, margin: '0 auto', padding: '20px 16px 80px' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: -.4, fontStyle: 'italic' }}>
            <span>Happy Hour</span><span style={{ color: '#E85D1A' }}> Unlocked</span>
            <span style={{ fontSize: 12, fontWeight: 400, color: '#888', marginLeft: 10, fontStyle: 'normal' }}>Admin</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 12, color: '#888' }}>{venues.length} venues · Cincinnati</div>
            <button onClick={handleLogout} style={{ ...btn('#F3F4F6', '#555'), border: '1px solid #EAE6DF', fontSize: 11 }}>Sign out</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid #EAE6DF', marginBottom: 24 }}>
          <TabBtn active={tab === 'pending'} onClick={() => setTab('pending')}>
            🔔 Pending approvals
            {pendingCount > 0 && <span style={{ background: '#E85D1A', color: '#fff', borderRadius: 20, padding: '1px 7px', fontSize: 10, marginLeft: 7, fontWeight: 800 }}>{pendingCount}</span>}
          </TabBtn>
          <TabBtn active={tab === 'analytics'} onClick={() => setTab('analytics')}>📊 Analytics</TabBtn>
          <TabBtn active={tab === 'ads'} onClick={() => setTab('ads')}>
            📣 Brand ads
            {brandAds.filter(a => a.is_active).length > 0 && (
              <span style={{ background: '#8B5CF6', color: '#fff', borderRadius: 20, padding: '1px 7px', fontSize: 10, marginLeft: 7, fontWeight: 800 }}>{brandAds.filter(a => a.is_active).length} live</span>
            )}
          </TabBtn>
          <TabBtn active={tab === 'weekly'} onClick={() => setTab('weekly')}>🏆 Weekly pick</TabBtn>
        </div>

        {/* ══ PENDING TAB ══════════════════════════════════════════════════════ */}
        {tab === 'pending' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {(['pending', 'approved', 'rejected', 'all'] as const).map(f => (
                <button key={f} onClick={() => setFilter(f)} style={{ ...btn(filter === f ? '#3A3630' : '#fff', filter === f ? '#fff' : '#888'), border: '1px solid #EAE6DF' }}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                  <span style={{ marginLeft: 5, opacity: .7 }}>({f === 'all' ? contributions.length : contributions.filter(c => c.status === f).length})</span>
                </button>
              ))}
              <button onClick={loadContributions} style={{ ...btn('#fff', '#888'), border: '1px solid #EAE6DF', marginLeft: 'auto' }}>↻ Refresh</button>
            </div>

            {contribLoading && <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>Loading...</div>}

            {!contribLoading && filteredContribs.length === 0 && (
              <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>✓</div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>{filter === 'pending' ? 'No pending submissions' : 'Nothing here'}</div>
                <div style={{ fontSize: 13 }}>{filter === 'pending' ? 'New venue submissions will appear here.' : 'Try a different filter.'}</div>
              </div>
            )}

            {!contribLoading && filteredContribs.map(contrib => {
              const d = contrib.data || {}
              const isNew = contrib.flow === 'new_venue'
              const isClaim = isClaimContribution(contrib)
              const isEdit = !isNew && !isClaim
              const isExpanded = expandedId === contrib.id
              const isPending = contrib.status === 'pending'

              const title = isNew ? (d.name || 'Unnamed venue')
                          : isClaim ? `Claim: ${d.venue_name || 'Unknown venue'}`
                          : `Edit: ${d.venue_name || 'Unknown'}`

              const badge = isNew ? { label: '+ New venue', bg: '#EFF6FF', color: '#1E40AF' }
                          : isClaim ? { label: '👤 Ownership claim', bg: '#F3EEFF', color: '#6B21A8' }
                          : { label: '✏️ Edit suggestion', bg: '#FFF8E8', color: '#7A5000' }

              const subline = isNew ? `${d.neighborhood || '—'} · ${d.city || 'Cincinnati'}`
                            : isClaim ? `${d.owner_name || 'Unknown'} (${d.owner_role || 'owner'}) · ${d.owner_email || d.owner_phone || 'no contact given'}`
                            : (d.field_suggestions || '').slice(0, 80)

              return (
                <div key={contrib.id} style={{ ...card, borderLeft: `4px solid ${isPending ? '#E85D1A' : contrib.status === 'approved' ? '#22C55E' : '#ddd'}` }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: '#1A1612' }}>{title}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: badge.bg, color: badge.color }}>{badge.label}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: isPending ? '#FFF3CD' : contrib.status === 'approved' ? '#DCFCE7' : '#F3F4F6', color: isPending ? '#856404' : contrib.status === 'approved' ? '#15803D' : '#6B7280' }}>{contrib.status}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#888' }}>
                        {subline}
                        <span style={{ marginLeft: 8, color: '#bbb' }}>{new Date(contrib.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                      <button onClick={() => setExpandedId(isExpanded ? null : contrib.id)} style={{ ...btn('#F8F6F1', '#555'), border: '1px solid #EAE6DF' }}>{isExpanded ? 'Hide' : 'View details'}</button>
                      {isPending && isNew && <button onClick={() => approveVenue(contrib)} disabled={actionLoading === contrib.id} style={btn('#22C55E', '#fff')}>{actionLoading === contrib.id ? 'Publishing...' : '✓ Approve & publish'}</button>}
                      {isPending && isClaim && <button onClick={() => approveClaim(contrib)} disabled={actionLoading === contrib.id} style={btn('#8B5CF6', '#fff')}>{actionLoading === contrib.id ? 'Linking...' : '✓ Approve claim'}</button>}
                      {isPending && isEdit && <button onClick={() => markEditHandled(contrib)} disabled={actionLoading === contrib.id} style={btn('#F3F4F6', '#555')} title="No auto-apply exists for free-text edits — make the change in Edit Venue (Analytics tab), then mark this handled.">✓ Mark reviewed</button>}
                      {isPending && <button onClick={() => rejectContribution(contrib)} disabled={actionLoading === contrib.id} style={btn('#fee2e2', '#c0392b')}>✕ Reject</button>}
                      {!isPending && <button onClick={() => deleteContribution(contrib.id)} style={btn('#F3F4F6', '#9CA3AF')}>🗑 Delete</button>}
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #EAE6DF' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                        {Object.entries(d).filter(([k]) => k !== 'flow' && k !== 'schedules').map(([key, val]) => (
                          <div key={key} style={{ background: '#F8F6F1', borderRadius: 8, padding: '8px 12px' }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3 }}>{key.replace(/_/g, ' ')}</div>
                            <div style={{ fontSize: 12, color: '#333', wordBreak: 'break-word' as const }}>{String(val || '—')}</div>
                          </div>
                        ))}
                      </div>
                      {d.schedules && d.schedules.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', marginBottom: 6 }}>Schedules ({d.schedules.length})</div>
                          {d.schedules.map((s: any, i: number) => (
                            <div key={i} style={{ background: '#F8F6F1', borderRadius: 8, padding: '8px 12px', marginBottom: 6 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#333' }}>{s.days?.join(', ')} · {s.start_time}–{s.end_time}</div>
                              <div style={{ fontSize: 11, color: '#666' }}>{s.deal_text}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {isPending && isNew && (
                        <div style={{ marginTop: 10, padding: '10px 14px', background: '#FFFBEB', borderRadius: 8, border: '1px solid #FDE68A', fontSize: 12, color: '#92400E' }}>
                          ⚠️ Approving will publish this venue immediately with all submitted schedules.
                        </div>
                      )}
                      {isPending && isClaim && (
                        <div style={{ marginTop: 10, padding: '10px 14px', background: '#F5F0FF', borderRadius: 8, border: '1px solid #DDD0FF', fontSize: 12, color: '#5B21B6' }}>
                          👤 Approving links this contact to the venue permanently (visible any time in venue_owners), separate from this queue entry.
                        </div>
                      )}
                      {isPending && isEdit && (
                        <div style={{ marginTop: 10, padding: '10px 14px', background: '#FFFBEB', borderRadius: 8, border: '1px solid #FDE68A', fontSize: 12, color: '#92400E' }}>
                          ✏️ This is free-text — nothing applies it automatically. Open Edit Venue for "{d.venue_name}" in the Analytics tab to make the actual change, then come back and mark this reviewed.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* ══ ADS TAB ══════════════════════════════════════════════════════════ */}
        {tab === 'ads' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1612' }}>Brand advertisements</div>
              <button onClick={() => setEditingAd(EMPTY_AD)} style={{ ...btn('#E85D1A', '#fff'), padding: '8px 16px' }}>+ New ad</button>
            </div>

            {editingAd && (
              <div style={{ ...card, border: '2px solid #E85D1A', marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#1A1612', marginBottom: 14 }}>{editingAd.id ? 'Edit ad' : 'Create new ad'}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  {([['brand_name','Brand name *','e.g. Modelo'],['headline','Headline *','e.g. Enjoy Modelo tonight'],['subtext','Subtext','e.g. Find it on draft at bars near you'],['logo_bg_color','Logo background color','#E85D1A'],['position','Position (order)','0']] as [string,string,string][]).map(([field,label,placeholder]) => (
                    <div key={field}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
                      <input value={(editingAd as any)[field] ?? ''} onChange={e => setEditingAd(prev => ({ ...prev, [field]: field === 'position' ? parseInt(e.target.value) || 0 : e.target.value }))} placeholder={placeholder}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #E0DDD8', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' as const }} />
                    </div>
                  ))}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>Brand logo</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {editingAd?.logo_url && (
                        <div style={{ width: 48, height: 48, borderRadius: 8, background: editingAd.logo_bg_color || '#E85D1A', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                          <img src={editingAd.logo_url} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
                        </div>
                      )}
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#F8F6F1', border: '1px solid #E0DDD8', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#555' }}>
                        📁 {editingAd?.logo_url ? 'Change logo' : 'Upload logo'}
                        <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                      </label>
                      {editingAd?.logo_url && <button onClick={() => setEditingAd(prev => ({ ...prev, logo_url: '' }))} style={{ ...btn('#fee2e2', '#c0392b'), fontSize: 11 }}>Remove</button>}
                    </div>
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>PNG, JPG, or SVG. Square logos work best. Uploaded to Supabase Storage.</div>
                  </div>
                </div>
                {editingAd.headline && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#888', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' }}>Preview</div>
                    <div style={{ background: '#3A3630', borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{ width: 48, height: 48, borderRadius: 10, background: editingAd.logo_bg_color || '#E85D1A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                        {editingAd.logo_url ? <img src={editingAd.logo_url} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} /> : <span style={{ fontSize: 11, fontWeight: 800, color: '#fff' }}>{(editingAd.brand_name || 'AD').slice(0, 2).toUpperCase()}</span>}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', marginBottom: 2 }}>{editingAd.headline}</div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,.55)' }}>{editingAd.subtext}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 9, color: '#aaa', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginTop: 3 }}>Sponsored</div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => setEditingAd(null)} style={{ ...btn('#F3F4F6', '#555'), padding: '8px 16px' }}>Cancel</button>
                  <button onClick={handleSaveAd} disabled={adSaving} style={{ ...btn('#E85D1A', '#fff'), padding: '8px 16px' }}>{adSaving ? 'Saving...' : 'Save ad'}</button>
                </div>
              </div>
            )}

            {brandAds.length === 0 && !editingAd && (
              <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>📣</div>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>No brand ads yet</div>
                <div style={{ fontSize: 13 }}>Create your first ad to start showing sponsored banners between venue cards.</div>
              </div>
            )}

            {brandAds.map(ad => (
              <div key={ad.id} style={{ ...card, borderLeft: `4px solid ${ad.is_active ? '#22C55E' : '#ddd'}` }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 8, background: ad.logo_bg_color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                      {ad.logo_url ? <img src={ad.logo_url} alt={ad.brand_name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }} /> : <span style={{ fontSize: 11, fontWeight: 800, color: '#fff' }}>{ad.brand_name.slice(0, 2).toUpperCase()}</span>}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#1A1612', marginBottom: 2 }}>{ad.brand_name}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>{ad.headline}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <div onClick={() => handleToggleAd(ad)} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}>
                      <div style={{ width: 36, height: 20, borderRadius: 20, background: ad.is_active ? '#22C55E' : '#E0DDD8', position: 'relative', transition: 'background .2s' }}>
                        <div style={{ position: 'absolute', top: 2, left: ad.is_active ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .2s' }} />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: ad.is_active ? '#22C55E' : '#aaa' }}>{ad.is_active ? 'Live' : 'Off'}</span>
                    </div>
                    <button onClick={() => setEditingAd(ad)} style={{ ...btn('#F8F6F1', '#555'), border: '1px solid #EAE6DF' }}>Edit</button>
                    <button onClick={() => handleDeleteAd(ad.id)} style={btn('#fee2e2', '#c0392b')}>🗑</button>
                  </div>
                </div>
                {adStats[ad.id] ? (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #EAE6DF' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 12 }}>
                      {[
                        { label: 'IMPRESSIONS', value: adStats[ad.id].impressions.toLocaleString(), color: '#3B82F6' },
                        { label: 'CLICKS',      value: adStats[ad.id].clicks.toLocaleString(),      color: '#E85D1A' },
                        { label: 'CTR',         value: `${adStats[ad.id].ctr}%`,                    color: '#8B5CF6' },
                        { label: 'AVG / DAY',   value: adStats[ad.id].days > 0
                            ? Math.round(adStats[ad.id].impressions / adStats[ad.id].days).toLocaleString()
                            : '—', color: '#22C55E' },
                      ].map(s => (
                        <div key={s.label} style={{ textAlign: 'center', background: '#F8F6F1', borderRadius: 10, padding: '10px 6px' }}>
                          <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                          <div style={{ fontSize: 9, color: '#aaa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', marginTop: 2 }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#bbb', marginBottom: 4 }}>
                        <span>Impression volume · last 30 days</span>
                        <span>{adStats[ad.id].days} days active</span>
                      </div>
                      <div style={{ height: 6, background: '#EAE6DF', borderRadius: 3 }}>
                        <div style={{ height: 6, borderRadius: 3, background: '#3B82F6', width: `${Math.min(100, Math.round((adStats[ad.id].impressions / Math.max(...Object.values(adStats).map(s => s.impressions), 1)) * 100))}%`, transition: 'width .4s' }} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #EAE6DF', fontSize: 12, color: '#bbb' }}>No data yet — stats appear once the ad goes live</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ══ ANALYTICS TAB ════════════════════════════════════════════════════ */}
        {/* ══ WEEKLY PICK TAB ══════════════════════════════════════════════ */}
        {tab === 'weekly' && (
          <WeeklyFeatureManager venues={venues} brandAds={brandAds} />
        )}

        {tab === 'analytics' && (
          <div>

            {/* ── AUDIENCE (device-based) ── */}
            <div style={{ ...card, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1612' }}>Audience — unique devices</div>
                <button onClick={() => { loadDeviceStats(); loadAnalytics(); loadGrowthAndDeals(); }} style={{ ...btn('#F8F6F1', '#555'), border: '1px solid #EAE6DF', fontSize: 11 }}>
                  {deviceStatsLoading ? 'Loading...' : '↻ Refresh'}
                </button>
              </div>

              {deviceStats ? (
                <>
                  {/* Summary stat cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
                    <StatCard label="MAU" value={deviceStats.mau.toLocaleString()} color="#E85D1A" sub="this month" />
                    <StatCard label="Today" value={todayDeviceCount.toLocaleString()} color="#3B82F6" sub="unique devices" />
                    <StatCard label="New" value={deviceStats.newDevices30d.toLocaleString()} color="#22C55E" sub="this month" />
                    <StatCard label="Returning" value={deviceStats.returningDevices30d.toLocaleString()} color="#8B5CF6" sub="this month" />
                  </div>

                  {/* New vs returning bar */}
                  {(deviceStats.newDevices30d + deviceStats.returningDevices30d) > 0 && (() => {
                    const total = deviceStats.newDevices30d + deviceStats.returningDevices30d
                    const newPct = Math.round((deviceStats.newDevices30d / total) * 100)
                    const retPct = 100 - newPct
                    return (
                      <div style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#888', marginBottom: 6 }}>
                          <span>🟢 New {newPct}%</span>
                          <span>🟣 Returning {retPct}%</span>
                        </div>
                        <div style={{ height: 10, borderRadius: 5, overflow: 'hidden', display: 'flex' }}>
                          <div style={{ width: `${newPct}%`, background: '#22C55E', transition: 'width .4s' }} />
                          <div style={{ flex: 1, background: '#8B5CF6' }} />
                        </div>
                      </div>
                    )
                  })()}

                  {/* Daily devices chart */}
                  {deviceStats.dailyDevices.some(d => d.devices > 0) && (() => {
                    const max = Math.max(...deviceStats.dailyDevices.map(d => d.devices), 1)
                    const peak = Math.max(...deviceStats.dailyDevices.map(d => d.devices))
                    return (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>Daily active devices — last 30 days</div>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 70, marginBottom: 4 }}>
                          {deviceStats.dailyDevices.map((d, i) => (
                            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                              <div
                                title={`${d.date}: ${d.devices} devices`}
                                style={{
                                  width: '100%', borderRadius: '3px 3px 0 0',
                                  background: d.devices === peak ? '#E85D1A' : '#3B82F6',
                                  height: `${Math.max((d.devices / max) * 100, d.devices > 0 ? 4 : 1)}%`,
                                  opacity: d.devices === 0 ? 0.2 : 1,
                                  transition: 'height .3s',
                                }}
                              />
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#bbb' }}>
                          <span>{deviceStats.dailyDevices[0]?.date}</span>
                          <span>{deviceStats.dailyDevices[14]?.date}</span>
                          <span>{deviceStats.dailyDevices[29]?.date}</span>
                        </div>
                      </>
                    )
                  })()}

                  {!deviceStats.dailyDevices.some(d => d.devices > 0) && (
                    <div style={{ fontSize: 12, color: '#bbb', textAlign: 'center', padding: '12px 0' }}>
                      Device chart shows after today's sessions sync — check back in a few minutes
                    </div>
                  )}
                </>
              ) : deviceStatsLoading ? (
                <div style={{ textAlign: 'center', color: '#888', padding: 20, fontSize: 13 }}>Loading audience data...</div>
              ) : (
                <div style={{ fontSize: 12, color: '#bbb', textAlign: 'center', padding: 20 }}>
                  No device analytics yet — deploy deviceAnalytics.ts and visit the app to start tracking
                </div>
              )}
            </div>

            {/* ── GROWTH + DEALS VIEWS ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>

              {/* Monthly Growth Rate */}
              <div style={{ ...card, marginBottom: 0, padding: 16, textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', letterSpacing: '0.08em', marginBottom: 8 }}>MONTH OVER MONTH</div>
                {(() => {
                  const dayOfMonth = new Date().getDate()
                  const isEarlyMonth = dayOfMonth <= 7

                  if (monthlyGrowth.rate === null) {
                    return (
                      <div>
                        <div style={{ fontSize: 26, fontWeight: 800, color: '#888' }}>—</div>
                        <div style={{ fontSize: 11, color: '#bbb', marginTop: 4 }}>Need prior month data</div>
                      </div>
                    )
                  }

                  if (isEarlyMonth) {
                    return (
                      <div>
                        <div style={{ fontSize: 26, fontWeight: 800, color: '#888' }}>—</div>
                        <div style={{ fontSize: 11, color: '#bbb', marginTop: 4 }}>
                          {monthlyGrowth.thisMonthLabel} just started ({dayOfMonth} day{dayOfMonth > 1 ? 's' : ''} in)
                        </div>
                        <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                          {monthlyGrowth.lastMonthLabel}: {monthlyGrowth.lastMonth.toLocaleString()} appearances
                        </div>
                      </div>
                    )
                  }

                  return (
                    <div>
                      <div style={{ fontSize: 26, fontWeight: 800, color: monthlyGrowth.rate >= 0 ? '#22C55E' : '#E24B4A' }}>
                        {monthlyGrowth.rate >= 0 ? '+' : ''}{monthlyGrowth.rate}%
                      </div>
                      <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                        {monthlyGrowth.lastMonthLabel} {monthlyGrowth.lastMonth.toLocaleString()} → {monthlyGrowth.thisMonthLabel} {monthlyGrowth.thisMonth.toLocaleString()}
                      </div>
                    </div>
                  )
                })()}
              </div>

              {/* Deals Views */}
              <div style={{ ...card, marginBottom: 0, padding: 16, textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', letterSpacing: '0.08em', marginBottom: 8 }}>DEALS VIEWS</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#8B5CF6' }}>{dealsViews.toLocaleString()}</div>
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>venue detail opens · this month</div>
              </div>

            </div>

            {/* ── SESSION TRAFFIC CHART (existing) ── */}
            {trafficData.length > 0 && (() => {
              const max = Math.max(...trafficData.map(d => d.visitors), 1)
              const total = trafficData.reduce((a, d) => a + d.visitors, 0)
              const now = new Date()
              const daysElapsed = now.getDate() // days elapsed this calendar month
              const avg = daysElapsed > 0 ? Math.round(total / daysElapsed) : 0
              const peak = Math.max(...trafficData.map(d => d.visitors))
              const last7 = last7Visits // rolling 7-day window — independent from calendar month chart
              return (
                <div style={{ ...card, marginBottom: 20 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1612', marginBottom: 14 }}>Daily Visits — this month</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
                    {[
                      { label: 'Total visits', value: total.toLocaleString(), color: '#3B82F6' },
                      { label: 'Avg per day', value: avg.toLocaleString(), color: '#E85D1A' },
                      { label: 'Last 7 days', value: last7.toLocaleString(), color: '#22C55E' },
                    ].map(s => (
                      <div key={s.label} style={{ background: '#F8F6F1', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 70, marginBottom: 4 }}>
                    {trafficData.map((d, i) => (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                        <div title={`${d.date}: ${d.visitors}`} style={{ width: '100%', borderRadius: '3px 3px 0 0', background: d.visitors === peak ? '#E85D1A' : '#3B82F6', height: `${Math.max((d.visitors / max) * 100, d.visitors > 0 ? 4 : 1)}%`, opacity: d.visitors === 0 ? 0.2 : 1, transition: 'height .3s' }} />
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#bbb' }}>
                    <span>{trafficData[0]?.date}</span><span>{trafficData[14]?.date}</span><span>{trafficData[29]?.date}</span>
                  </div>
                </div>
              )
            })()}

            {/* ── TOP VENUES BY APPEARANCES ── */}
            <div style={{ ...card, marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#1A1612' }}>Top Venues by Appearances</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['week', 'month'] as const).map(r => (
                    <button
                      key={r}
                      onClick={() => setTopVenuesRange(r)}
                      style={{
                        padding: '5px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        fontFamily: 'inherit', fontSize: 11, fontWeight: 700,
                        background: topVenuesRange === r ? '#1A1612' : '#F8F6F1',
                        color: topVenuesRange === r ? '#fff' : '#888',
                      }}
                    >
                      {r === 'week' ? 'Last 7 days' : 'This month'}
                    </button>
                  ))}
                </div>
              </div>
              {(() => {
                const sorted = venues
                  .map(v => ({ venue: v, count: topVenuesData[v.id] || 0 }))
                  .filter(x => x.count > 0)
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 10)

                if (sorted.length === 0) {
                  return <div style={{ textAlign: 'center', color: '#aaa', padding: '20px 0', fontSize: 13 }}>No impression data yet</div>
                }

                const max = sorted[0].count

                return (
                  <div>
                    {sorted.map(({ venue, count }, i) => (
                      <div key={venue.id} style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{
                              width: 20, height: 20, borderRadius: '50%',
                              background: i === 0 ? '#E85D1A' : i === 1 ? '#888' : i === 2 ? '#c8a87a' : '#eee',
                              color: i < 3 ? '#fff' : '#999',
                              fontSize: 10, fontWeight: 800,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0,
                            }}>{i + 1}</span>
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1612' }}>{venue.name}</div>
                              <div style={{ fontSize: 11, color: '#aaa' }}>{venue.neighborhood}</div>
                            </div>
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 800, color: '#E85D1A' }}>{count}</div>
                        </div>
                        <div style={{ height: 4, background: '#F0EDE8', borderRadius: 2 }}>
                          <div style={{
                            height: 4, borderRadius: 2,
                            background: i === 0 ? '#E85D1A' : '#1A1612',
                            width: `${Math.round((count / max) * 100)}%`,
                            transition: 'width 0.4s ease',
                          }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )
              })()}
            </div>

            {/* ── SUMMARY TOTALS ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
              
              <StatCard label="Direction Clicks" value={totalDirections} color="#3B82F6" />
              <StatCard label="Favorites" value={totalFavorites} color="#E24B4A" />
              <StatCard label="Deals Viewed" value={totalDealsViewed} color="#8B5CF6" />
            </div>

            {/* ── WEEK / MONTH / YEAR NAVIGATOR — drives everything below, including impressions/venue views ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1612' }}>Venues</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 4, background: '#F8F6F1', borderRadius: 8, padding: 3 }}>
                  {(['week', 'month', 'year'] as const).map(m => (
                    <button
                      key={m}
                      onClick={() => { setStatsRange(m); setRangeOffset(0) }}
                      style={{
                        padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                        fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
                        background: statsRange === m ? '#1A1612' : 'transparent',
                        color: statsRange === m ? '#fff' : '#888',
                      }}
                    >
                      {m === 'week' ? 'Weekly' : m === 'month' ? 'Monthly' : 'Yearly'}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setRangeOffset(o => o - 1)}
                  style={{ ...btn('#fff', '#555'), border: '1px solid #E0DDD8' }}
                >← Prev</button>
                <div style={{ padding: '7px 14px', borderRadius: 8, background: '#3A3630', color: '#fff', fontSize: 13, fontWeight: 700 }}>{getWeekLabel()}</div>
                <button
                  onClick={() => setRangeOffset(o => Math.min(0, o + 1))}
                  disabled={rangeOffset === 0}
                  style={{ ...btn('#fff', '#555'), border: '1px solid #E0DDD8', opacity: rangeOffset === 0 ? .4 : 1 }}
                >Next →</button>
              </div>
            </div>


            {/* ── SEARCH + SORT ── */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search venues..."
                style={{ flex: 1, minWidth: 180, padding: '9px 12px', borderRadius: 9, border: '1px solid #E0DDD8', fontSize: 13, fontFamily: 'inherit' }} />
              <select value={sortBy} onChange={e => setSortBy(e.target.value as any)}
                style={{ padding: '9px 12px', borderRadius: 9, border: '1px solid #E0DDD8', fontSize: 13, fontFamily: 'inherit', background: '#fff' }}>
                <option value="views">Sort by views</option>
                <option value="clicks">Sort by directions</option>
                <option value="name">Sort by name</option>
              </select>
            </div>

            {/* ── VENUE CARDS ── */}
            {analyticsLoading ? (
              <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>Loading...</div>
            ) : filteredVenues.map(venue => {
              const s = getVenueStat(venue.id)
              const appearances = impressionData[venue.id] || 0
              const maxImpressions = Math.max(...Object.values(impressionData), 1)
              const isExpanded = expandedId === venue.id
              const impressionRangeLabel = getWeekLabel()

              return (
                <div key={venue.id} style={{ ...card, borderLeft: `4px solid ${venue.is_featured ? '#E85D1A' : (venue as any).is_sponsored ? '#8B5CF6' : '#EAE6DF'}`, transition: 'box-shadow .15s' }}>
                  <div onClick={() => setExpandedId(isExpanded ? null : venue.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, cursor: 'pointer' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#1A1612', marginBottom: 2 }}>{venue.name}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>
                        {venue.neighborhood} · {venue.city}
                        {venue.is_featured && <span style={{ marginLeft: 8, background: '#FFF3E8', color: '#E85D1A', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20 }}>Featured</span>}
                        {(venue as any).is_sponsored && <span style={{ marginLeft: 6, background: '#F3EEFF', color: '#8B5CF6', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20 }}>Sponsored</span>}
                      </div>
                    </div>
                    {!isExpanded && (
                      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexShrink: 0 }}>
                        <div style={{ textAlign: 'center' }}><div style={{ fontSize: 18, fontWeight: 800, color: '#888' }}>{appearances}</div><div style={{ fontSize: 9, color: '#bbb', fontWeight: 600, textTransform: 'uppercase' }}>Impressions</div></div>
                        <div style={{ textAlign: 'center' }}><div style={{ fontSize: 18, fontWeight: 800, color: '#E85D1A' }}>{s.detail_views}</div><div style={{ fontSize: 9, color: '#bbb', fontWeight: 600, textTransform: 'uppercase' }}>Venue views</div></div>
                        <div style={{ fontSize: 18, color: '#ccc' }}>›</div>
                      </div>
                    )}
                    {isExpanded && <div style={{ fontSize: 18, color: '#ccc', transform: 'rotate(90deg)' }}>›</div>}
                  </div>

                  {isExpanded && (
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #EAE6DF' }}>
                      <div style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '.04em' }}>Venue Appearances ({impressionRangeLabel})</div>
                          <div style={{ fontSize: 16, fontWeight: 800, color: '#1A1612' }}>{appearances.toLocaleString()}</div>
                        </div>
                        <div style={{ height: 10, background: '#F8F6F1', borderRadius: 5, overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: 5, background: '#E85D1A', width: `${Math.max((appearances / maxImpressions) * 100, appearances > 0 ? 3 : 0)}%`, transition: 'width .4s' }} />
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
                        {[
                          { label: 'Directions',  value: s.directions_clicks,  color: '#3B82F6' },
                          { label: 'Website',     value: s.website_clicks,     color: '#8B5CF6' },
                          { label: 'Saves',       value: s.favorites,          color: '#E24B4A' },
                          { label: 'Deals Viewed',      value: detailViewData[venue.id] ?? 0, color: '#8B5CF6' },
                          { label: 'Venue Appearances', value: impressionData[venue.id] ?? 0,  color: '#888'    },
                        ].map(stat => (
                          <div key={stat.label} style={{ background: '#F8F6F1', borderRadius: 10, padding: '12px 14px', textAlign: 'center' }}>
                            <div style={{ fontSize: 22, fontWeight: 800, color: stat.color }}>{stat.value}</div>
                            <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em', marginBottom: 2 }}>{stat.label}</div>
                            <div style={{ fontSize: 9, color: '#ccc' }}>{getWeekLabel().toLowerCase()}</div>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                        <div style={{ display: 'flex', gap: 20 }}>
                          <Toggle on={venue.is_featured} onChange={() => toggleVenueFlag(venue, 'is_featured')} label="Featured" color="#E85D1A" />
                          <Toggle on={(venue as any).is_sponsored} onChange={() => toggleVenueFlag(venue, 'is_sponsored')} label="Sponsored" color="#8B5CF6" />
                          {toggling?.startsWith(venue.id) && <span style={{ fontSize: 11, color: '#aaa' }}>Saving...</span>}
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => setEditingVenueId(editingVenueId === venue.id ? null : venue.id)} style={btn(editingVenueId === venue.id ? '#F3F4F6' : '#F8F6F1', '#555', { border: '1px solid #EAE6DF' })}>
                            {editingVenueId === venue.id ? '✕ Close editor' : '✏️ Edit venue'}
                          </button>
                          <button onClick={() => sendStatsEmail(venue)} disabled={sending === venue.id} style={btn('#3A3630', '#fff')}>
                            {sending === venue.id ? 'Opening...' : '📧 Email stats'}
                          </button>
                          <button onClick={() => deleteVenue(venue)} style={btn('#fee2e2', '#c0392b')}>🗑 Delete</button>
                        </div>
                      </div>

                      {editingVenueId === venue.id && (
                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #EAE6DF' }}>
                          <EditVenueForm venue={venue} onClose={() => setEditingVenueId(null)} onSaved={() => { setEditingVenueId(null); loadAnalytics() }} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}

          </div>
        )}

      </div>
    </>
  )
}
